import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { PRODUCT_TABS } from "@/data/content";

const TABS = [PRODUCT_TABS.solo, PRODUCT_TABS.brokerage];

export const ProductTabs = () => {
  const [active, setActive] = useState("solo");
  const tab = active === "solo" ? PRODUCT_TABS.solo : PRODUCT_TABS.brokerage;

  return (
    <section id="product" className="py-24 md:py-32 border-t border-ink/10" data-testid="product-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-coral mb-4">Brokerage management software</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-ink max-w-3xl">
            Built for the solo hustle — and the whole team.
          </h2>
        </Reveal>

        {/* Tab switcher */}
        <div className="mt-10 inline-flex border border-ink/15 p-1 bg-bone" data-testid="product-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              data-testid={`tab-${t.id}`}
              className="relative px-5 sm:px-7 py-3 text-sm font-medium transition-colors duration-300"
            >
              {active === t.id && (
                <motion.span
                  layoutId="product-tab-pill"
                  className="absolute inset-0 bg-ink"
                  transition={{ type: "spring", stiffness: 400, damping: 34 }}
                />
              )}
              <span className={`relative z-10 ${active === t.id ? "text-bone" : "text-ink/60"}`}>
                {t.label}
              </span>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center"
          >
            <div className="lg:col-span-4">
              <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight italic">
                {tab.sub}
              </h3>
              <ul className="mt-8 space-y-4">
                {tab.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-ink/80">
                    <span className="w-5 h-5 shrink-0 flex items-center justify-center bg-coral text-bone rounded-full">
                      <Check className="w-3 h-3" />
                    </span>
                    <span className="text-sm md:text-base">{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-8 grid grid-cols-3 gap-3 md:gap-4">
              {tab.shots.map((src, i) => (
                <div
                  key={i}
                  className={`overflow-hidden border border-ink/10 ${
                    i === 1 ? "translate-y-6" : ""
                  }`}
                >
                  <img
                    src={src}
                    alt={`${tab.label} product screenshot ${i + 1}`}
                    className="w-full h-full object-cover object-top aspect-[3/4]"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
